import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import {
  auth,
  db,
  signInWithGoogle as firebaseSignInWithGoogle,
  signInWithFacebook as firebaseSignInWithFacebook,
  signInWithPhone as firebaseSignInWithPhone,
  loginWithEmail as firebaseLoginWithEmail,
  registerWithEmail as firebaseRegisterWithEmail,
  getRedirectResult,
} from '../firebase';
import { doc, setDoc, serverTimestamp, getDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { messagingService } from '../services/messagingService';
import { analyticsService } from '../services/analyticsService';

import { UserProfile } from '../types/user';

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  isAdmin: boolean;
  isSupport: boolean;
  isFounder: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithFacebook: () => Promise<void>;
  signInWithPhone: (phoneNumber: string, appVerifier: any) => Promise<any>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (
    email: string,
    password: string,
    fullName: string,
    role: 'buyer' | 'seller'
  ) => Promise<void>;
  logout: () => Promise<void>;
  isAuthModalOpen: boolean;
  authModalTab: 'login' | 'signup';
  openAuthModal: (tab?: 'login' | 'signup') => void;
  closeAuthModal: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  loading: true,
  isAdmin: false,
  isSupport: false,
  isFounder: false,
  signInWithGoogle: async () => {},
  signInWithFacebook: async () => {},
  signInWithPhone: async () => {},
  signInWithEmail: async () => {},
  signUpWithEmail: async () => {},
  logout: async () => {},
  isAuthModalOpen: false,
  authModalTab: 'login',
  openAuthModal: () => {},
  closeAuthModal: () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalTab, setAuthModalTab] = useState<'login' | 'signup'>('login');

  const openAuthModal = (tab: 'login' | 'signup' = 'login') => {
    setAuthModalTab(tab);
    setIsAuthModalOpen(true);
  };

  const closeAuthModal = () => {
    setIsAuthModalOpen(false);
  };

  useEffect(() => {
    // Process any redirect-based sign-in result after fallback
    getRedirectResult(auth)
      .then((result) => {
        if (result?.user) {
          console.log('Redirect login successful for:', result.user.email);
        }
      })
      .catch((error) => {
        console.error('Redirect sign-in failed:', error);
        // Sometimes cross-origin auth errors get stuck; sign out to clear bad state
        auth.signOut().catch(console.error);
        if (error.code !== 'auth/redirect-cancelled-by-user') {
          alert('Authentication failed during redirect. Please try again or use another login method.');
        }
      });

    // Track the Firestore real-time profile listener so we can clean it up
    let profileUnsubscribe: (() => void) | null = null;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);

      // Clean up any previous profile listener when auth state changes
      if (profileUnsubscribe) {
        profileUnsubscribe();
        profileUnsubscribe = null;
      }

      if (user) {
        const userRef = doc(db, 'users', user.uid);

        try {
          // One-time read to check if the document exists (for new user creation)
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Firestore timeout')), 8000)
          );
          const userSnap = (await Promise.race([getDoc(userRef), timeoutPromise])) as any;

          if (!userSnap.exists()) {
            // New user — create their profile document
            const savedRole = localStorage.getItem('pambo_signup_role') as
              | 'buyer'
              | 'seller'
              | null;
            const finalRole = savedRole === 'seller' ? 'seller' : 'buyer';
            localStorage.removeItem('pambo_signup_role');

            const referredBy = sessionStorage.getItem('pambo_referral_id') || null;
            if (referredBy) {
              sessionStorage.removeItem('pambo_referral_id');
            }

            const newProfile: UserProfile = {
              uid: user.uid,
              email: user.email || '',
              displayName: user.displayName || '',
              photoURL: user.photoURL || '',
              role: finalRole,
              plan: 'free',
              planExpiry: null,
              isLive: false,
              isVerified: false,
              pamboLiveExpiry: null,
              referredBy: referredBy,
              subscription: {
                plan: 'free',
                planWeight: 1,
                status: 'active',
                startedAt: Date.now(),
                expiresAt: Date.now() + 3650 * 24 * 60 * 60 * 1000,
                lastPaymentRef: 'INITIAL',
                autoRenew: false,
              },
            };

            // Auto-generate storeSlug for sellers
            if (finalRole === 'seller' && user.displayName) {
              try {
                const token = await user.getIdToken();
                const baseUrl = import.meta.env.VITE_API_URL || '';
                const res = await fetch(`${baseUrl}/api/seller/store-slug`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                  },
                  body: JSON.stringify({ displayName: user.displayName })
                });
                const data = await res.json();
                if (data.storeSlug) {
                  newProfile.storeSlug = data.storeSlug;
                }
              } catch (err) {
                console.error('Failed to assign store slug during signup:', err);
              }
            }

            await setDoc(userRef, { ...newProfile, createdAt: serverTimestamp(), isOnline: true });
            analyticsService.logUserAction('signup', {
              role: finalRole,
              method: 'social',
              referredBy,
            });
          }
        } catch (error) {
          console.error('Error during initial profile check:', error);
        }

        // ✅ Real-time listener — profile stays in sync with Firestore forever.
        // Now when a seller saves their phone number, it is instantly reflected
        // everywhere in the app — no more repeated "Profile Incomplete" prompts.
        profileUnsubscribe = onSnapshot(
          userRef,
          (snap) => {
            if (snap.exists()) {
              const userData = snap.data() as UserProfile;

              // Auto-heal founder role
              if (user.email === 'resellercecil@gmail.com' && userData.role !== 'founder') {
                updateDoc(userRef, { role: 'founder' }).catch(console.error);
                userData.role = 'founder';
              }

              if (userData.isBanned) {
                auth.signOut();
                setProfile(null);
                setUser(null);
                alert('Your account has been banned for violating our terms of service.');
              } else {
                setProfile(userData);
              }
            } else {
              setProfile(null);
            }
            setLoading(false);
          },
          (error) => {
            console.error('Error in profile real-time listener:', error);
            setLoading(false);
          }
        );
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
      if (profileUnsubscribe) profileUnsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!user) return;

    const userRef = doc(db, 'users', user.uid);
    const setOnline = async () => {
      try {
        await updateDoc(userRef, { isOnline: true, lastActive: serverTimestamp() });
      } catch (e) {
        console.error('Error setting online status:', e);
      }
    };
    const setOffline = async () => {
      try {
        await updateDoc(userRef, { isOnline: false, lastActive: serverTimestamp() });
      } catch (e) {
        console.error('Error setting offline status:', e);
      }
    };

    setOnline();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        setOffline();
      } else {
        setOnline();
      }
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', setOffline);

    return () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', setOffline);
      setOffline();
    };
  }, [user]);

  // ✅ REQUEST PUSH NOTIFICATION PERMISSION
  useEffect(() => {
    if (user) {
      // Delay slightly to not block initial load
      const timer = setTimeout(() => {
        messagingService.requestPermission();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  // 🔐 SECURITY: Only resellercecil@gmail.com is the platform founder/admin.
  // All admin privileges are gated on this email. Database role checks are kept
  // as a fallback but the email is the canonical source of truth.
  const OWNER_EMAIL = 'resellercecil@gmail.com';
  const isFounder = profile?.email === OWNER_EMAIL;
  const isAdmin = profile?.email === OWNER_EMAIL;
  const isSupport =
    profile?.email === OWNER_EMAIL ||
    profile?.role === 'support';

  const signInWithGoogle = async () => {
    await firebaseSignInWithGoogle();
    analyticsService.logUserAction('login', { method: 'google' });
  };

  const signInWithFacebook = async () => {
    await firebaseSignInWithFacebook();
    analyticsService.logUserAction('login', { method: 'facebook' });
  };

  const signInWithPhone = async (phoneNumber: string, appVerifier: any) => {
    const result = await firebaseSignInWithPhone(phoneNumber, appVerifier);
    analyticsService.logUserAction('login', { method: 'phone' });
    return result;
  };

  const signInWithEmail = async (email: string, password: string) => {
    await firebaseLoginWithEmail(email, password);
    analyticsService.logUserAction('login', { method: 'email' });
  };

  const signUpWithEmail = async (
    email: string,
    password: string,
    fullName: string,
    role: 'buyer' | 'seller'
  ) => {
    const userVal = await firebaseRegisterWithEmail(email, password, fullName);
    if (userVal) {
      const userRef = doc(db, 'users', userVal.uid);
      const referredBy = sessionStorage.getItem('pambo_referral_id') || null;
      if (referredBy) {
        sessionStorage.removeItem('pambo_referral_id');
      }

      const newProfile: UserProfile = {
        uid: userVal.uid,
        email: userVal.email || email,
        displayName: fullName,
        photoURL: userVal.photoURL || '',
        role: role,
        plan: 'free',
        planExpiry: null,
        isLive: false,
        isVerified: false,
        pamboLiveExpiry: null,
        referredBy: referredBy,
        subscription: {
          plan: 'free',
          planWeight: 1,
          status: 'active',
          startedAt: Date.now(),
          expiresAt: Date.now() + 3650 * 24 * 60 * 60 * 1000,
          lastPaymentRef: 'INITIAL',
          autoRenew: false,
        },
      };
      await setDoc(userRef, { ...newProfile, createdAt: serverTimestamp(), isOnline: true });
      setProfile(newProfile);
      analyticsService.logUserAction('signup', { role: role, method: 'email', referredBy });
    }
  };

  const logout = async () => {
    await auth.signOut();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading,
        isAdmin,
        isSupport,
        isFounder,
        signInWithGoogle,
        signInWithFacebook,
        signInWithPhone,
        signInWithEmail,
        signUpWithEmail,
        logout,
        isAuthModalOpen,
        authModalTab,
        openAuthModal,
        closeAuthModal,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
